import React, { useState, useEffect } from "react";
import Modal from "../../components/Modal/Modal";
import AddEntity from "../../components/AddEntity/AddEntity";
import ApiManager from "../../ApiManager/ApiManager";
import EditEntity from "../../components/EditEntity/EditEntity";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";

const EditExpenditure = ({ rowId, onSuccess, toggleFunction }) => {
  const [currentRowId, setCurrentRowId] = useState(rowId);
  const [carOptions, setCarOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [expenditureFields, setExpenditureFields] = useState([]);
  const [carMap, setCarMap] = useState({});
  const [driverMap, setDriverMap] = useState({});
  const [fileResponse, setFileResponse] = useState(null);
  const [expenditureData, setExpenditureData] = useState({});
  const [showLoading, setShowLoading] = useState(false);
  
  // Use the loading hook
  const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

  useEffect(() => {
    if (rowId) setCurrentRowId(rowId);
  }, [rowId]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setShowLoading(true);
    
    await withLoading('loadEditExpenditureData', async (onProgress) => {
      onProgress(10);
      await fetchDriverData();
      onProgress(40);
      await fetchCarData();
      onProgress(70);
      await fetchExpenditureData();
      onProgress(100);
      
      // Add a small delay to ensure the loading indicator is visible
      setTimeout(() => {
        setShowLoading(false);
      }, 500);
    }, 'Loading expenditure data...', 30000);
  };

  const fetchDriverData = async () => {
    try {
      const params = new URLSearchParams({
        page_size: 1000,
        page: 1,
      });
      const response = await ApiManager.getAllDrivers(params);
      if (!response.ok) throw new Error("Network response was not ok");
      const json = await response.json();
      const driverNames = json.map(
        (driver) => driver.firstName + " " + driver.lastName
      );
      const driverIdMap = json.reduce((map, driver) => {
        map[driver.firstName + " " + driver.lastName] = driver.id;
        return map;
      }, {});
      setDriverOptions(driverNames);
      setDriverMap(driverIdMap);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const fetchExpenditureData = async () => {
    try {
      const query = new URLSearchParams({
        id: currentRowId,
      });
      const response = await ApiManager.getExpenditure(query);
      if (!response.ok) throw new Error("Network response was not ok");
      const json = await response.json();
      setExpenditureData(json);
    } catch (error) {
      console.error("Error fetching expenditure:", error);
    }
  };

  const fetchCarData = async () => {
    try {
      const params = new URLSearchParams({
        page_size: 1000,
        page: 1,
      });
      const response = await ApiManager.getAllCars(params);
      if (!response.ok) throw new Error("Network response was not ok");
      const json = await response.json();
      const carNumbers = json.map((car) => car.vehicleNo);
      const carIdMap = json.reduce((map, car) => {
        map[car.vehicleNo] = car.id;
        return map;
      }, {});
      setCarOptions(carNumbers);
      setCarMap(carIdMap);
    } catch (error) {
      console.error("Error fetching cars:", error);
    }
  };

  useEffect(() => {
    setExpenditureFields([
      ...(carOptions.length > 0
        ? [
            {
              label: "Car",
              name: "car_number",
              type: "picklist",
              required: true,
              options: carOptions,
            },
          ]
        : [
            {
              label: "Car",
              name: "car_number",
              type: "picklist",
              required: true,
              options: [],
            },
          ]),
      ...(driverOptions.length > 0
        ? [
            {
              label: "Driver",
              name: "driver_name",
              type: "picklist",
              required: true,
              options: driverOptions,
            },
          ]
        : [
            {
              label: "Driver",
              name: "driver_name",
              type: "picklist",
              required: true,
              options: [],
            },
          ]),
      { label: "Amount", name: "amount", type: "number", required: true },
      { label: "Description", name: "description", type: "text", required: false },
      { label: "Receipt", name: "filepath", type: "file", required: false },
      { label: "Driver Paid", name: "is_paid_by_driver", type: "checkbox", required: false },
      { label: "Car Related", name: "is_car_related", type: "checkbox", required: false },
    ]);
  }, [carOptions, driverOptions]);

  const handleEditExpenditure = async (formData) => {
    const carId = carMap[formData.car_number];
    const driverId = driverMap[formData.driver_name];

    const updatedFormData = {
      ...formData,
      car_id: carId,
      driver_id: driverId,
    };

    if (fileResponse?.filepath) {
      updatedFormData.filepath = fileResponse.filepath;
    }

    const query = new URLSearchParams({
      id: currentRowId,
    });
    const response = await ApiManager.editExpenditure(query, updatedFormData);
    return response;
  };

  return (
    <>
      {showLoading ? (
        <div className="loading-container">
          <LoadingSpinner 
            size="large" 
            message={getMessage('loadEditExpenditureData') || "Loading expenditure data..."} 
            progress={getProgress('loadEditExpenditureData')}
            showProgress={true}
          />
        </div>
      ) : (
        <EditEntity
          prevData={expenditureData}
          updateFunction={handleEditExpenditure}
          columns={expenditureFields}
          toggleFunction={toggleFunction}
          setFileResponse={setFileResponse}
          fileResponse={fileResponse}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
};

export default EditExpenditure;
